/**
 * @fileoverview Companies List Service - Handles API interactions for companies data
 * @active Used in current Next.js App Router implementation
 *
 * This service is responsible for fetching and managing companies data from the API.
 * It uses RTK Query for data fetching and caching, and dispatches actions to the Redux store.
 *
 * Key features:
 * - Type-safe API interactions with proper error handling
 * - Request correlation IDs for debugging and tracing
 * - Detailed logging for request/response monitoring
 * - Automatic Redux store updates
 *
 * Data Flow:
 * 1. Component calls hook -> useCompaniesListRequest
 * 2. Hook calls this service
 * 3. Service fetches data and updates Redux store
 * 4. Components receive updates via selectors
 *
 * @see {@link src/store/newCompaniesList/newCompaniesList.slice.ts} - Redux slice for companies data
 * @see {@link src/store/newCompaniesList/newCompaniesList.selectors.ts} - Selectors for accessing store data
 * @see {@link src/hooks/useCompaniesListRequest.ts} - Hook for using this service
 * @see {@link src/components/companies/companiesList/newCompaniesList/index.tsx} - Main component using this service
 * @see {@link src/services/apiService/axiosService.ts} - Base API service used for requests
 * @see {@link src/services/apiService/aventureApi.ts} - RTK Query API definition
 * @see {@link src/services/error.service.ts} - Error handling utilities
 * @see {@link src/store/newLists/newLists.slice.ts} - Related slice for list metadata
 *
 * IMPORTANT: This file is client-side only. It uses browser-specific APIs and maintains
 * in-memory state that would cause hydration mismatches if used during SSR.
 */
// src/services/newCompaniesList.service.ts

import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { newCompanieListActions } from "@/store/newCompaniesList/newCompaniesList.slice";
import { newListActions } from "@/store/newLists/newLists.slice";

import { aventureApi } from "./apiService/aventureApi";
import { axiosService } from "./apiService/axiosService";
import { loadingError } from "./error.service";

/**
 * Helper to ensure error messages are always strings
 * @param error - The caught error object
 * @returns A string error message
 */
const getErrorMessage = (error: unknown): string => {
  // If error is already a string, return it
  if (typeof error === "string") return error;

  const defaultMessage = "Unknown API error";

  // Handle axios error structure
  if (error && typeof error === "object") {
    // Try to get error.response.data.message first
    if (
      "response" in error &&
      error.response &&
      typeof error.response === "object" &&
      "data" in error.response &&
      error.response.data &&
      typeof error.response.data === "object" &&
      "message" in error.response.data
    ) {
      const responseMessage = (error.response.data as Record<string, unknown>)
        .message;
      if (responseMessage && typeof responseMessage === "string") {
        return responseMessage;
      }
    }

    // Fall back to error.message
    if (
      "message" in error &&
      error.message &&
      typeof error.message === "string"
    ) {
      return error.message;
    }
  }

  return defaultMessage;
};

/**
 * Create a FetchBaseQueryError object from a string error message
 * This ensures compatibility with RTK Query's error handling
 *
 * @param errorMsg - Error message to convert
 * @returns FetchBaseQueryError compatible object
 */
const createErrorObject = (errorMsg: string): FetchBaseQueryError => {
  return {
    status: "CUSTOM_ERROR" as const,
    error: errorMsg,
    data: errorMsg,
  };
};

export const newCompanieListApi = aventureApi.injectEndpoints({
  endpoints: (builder) => ({
    companiesList: builder.mutation({
      queryFn: async (options, { dispatch }) => {
        // Add a unique request ID to track this specific request across logs
        const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

        // Debug log with request ID for correlation
        console.log(`[Request ${requestId}] Card view request:`, {
          endpoint: "/res/entity/filters/companies",
          params: options?.params,
          isCards: options?.isCards,
          timestamp: new Date().toISOString(),
        });

        try {
          const { data } = await axiosService.post(
            "/res/entity/filters/companies",
            options?.params
          );

          // Log successful response for debugging
          console.log(`[Request ${requestId}] Response success:`, {
            status: "success",
            dataItems: data?.data?.length || 0,
            page: data?.page,
            pages: data?.pages,
            total: data?.total,
          });

          dispatch(
            newCompanieListActions.setNewCompanies({
              data: data?.data,
              isCards: options?.isCards,
            })
          );
          dispatch(
            newListActions.setListParams({
              page: data?.page,
              pages: data?.pages,
              total: data?.total,
            })
          );

          return { data };
        } catch (error: unknown) {
          // Enhanced error logging with the same request ID for correlation
          // Safely access properties from the unknown error object
          const errorObj: Record<string, any> = {};

          // Only include object properties if error is an object
          if (error && typeof error === "object") {
            // Safe property access for logging
            errorObj.errorInfo = "info" in error ? error.info : undefined;
            errorObj.errorResponse =
              "response" in error ? error.response : undefined;
            errorObj.errorMessage =
              "message" in error ? error.message : "Unknown error";
          } else {
            errorObj.errorValue = error;
          }

          console.error(`[Request ${requestId}] Request failed:`, {
            ...errorObj,
            requestOptions: {
              endpoint: "/res/entity/filters/companies",
              params: options?.params,
              isCards: options?.isCards,
            },
          });

          const errorMessage = getErrorMessage(error);
          dispatch(loadingError(errorMessage));

          // Return RTK Query compatible error object
          return { error: createErrorObject(errorMessage) };
        }
      },
      invalidatesTags: ["companiesList"],
    }),
    companiesByList: builder.mutation({
      queryFn: async ({ params, key }, { dispatch }) => {
        // Add a unique request ID for correlation
        const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

        // Debug log with request ID for correlation
        console.log(`[Request ${requestId}] List view request:`, {
          endpoint: `app/list/${key}`,
          params,
          timestamp: new Date().toISOString(),
        });

        try {
          const { data } = await axiosService.post(`/app/list/${key}`, params);

          // Log successful response
          console.log(`[Request ${requestId}] Response success:`, {
            status: "success",
            dataItems: data?.data?.length || 0,
            page: data?.page,
            pages: data?.pages,
            total: data?.total,
          });

          dispatch(
            newCompanieListActions.setNewCompanies({ data: data?.data })
          );
          dispatch(
            newListActions.setListParams({
              page: data?.page,
              pages: data?.pages,
              total: data?.total,
            })
          );

          return { data };
        } catch (error: unknown) {
          // Enhanced error logging with the same request ID for correlation
          // Safely access properties from the unknown error object
          const errorObj: Record<string, any> = {};

          // Only include object properties if error is an object
          if (error && typeof error === "object") {
            // Safe property access for logging
            errorObj.errorInfo = "info" in error ? error.info : undefined;
            errorObj.errorResponse =
              "response" in error ? error.response : undefined;
            errorObj.errorMessage =
              "message" in error ? error.message : "Unknown error";
          } else {
            errorObj.errorValue = error;
          }

          console.error(`[Request ${requestId}] Request failed:`, {
            ...errorObj,
            requestOptions: {
              endpoint: `app/list/${key}`,
              params,
            },
          });

          const errorMessage = getErrorMessage(error);
          dispatch(loadingError(errorMessage));

          // Return RTK Query compatible error object
          return { error: createErrorObject(errorMessage) };
        }
      },
      invalidatesTags: ["companiesList"],
    }),
  }),
  overrideExisting: true,
});

export const { useCompaniesListMutation, useCompaniesByListMutation } =
  newCompanieListApi;

export default newCompanieListApi;
